import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  IconButton,
  useTheme,
  Grid2,
  Chip,
  Divider,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import SkuCopyRow from "../../../../../../views/components/common/SkuCopyRow";
import { Close as CloseIcon, Output as OutputIcon } from "@mui/icons-material";
import AppSearchDropdownMulti from "../../../../../../views/components/common/filter/AppSearchDropdownMulti";
import { FilterValue } from "../../../../../../models/Filters/FilterValue";
import { useStores } from "../../../../use-stores";
import { useNotification } from "../../../../../../utils/NotificationsContext";
import CustomTextField from "../../../../../../views/components/common/textFields/TextField";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import {
  SwageazyInventoryOutwardRequestFormData,
  SwageazyInventorySkuLineItem,
} from "../../../../../../models/SwageazyInventory/SwageazyInventory";

interface SwageazyInventoryOutwardPopupProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
}

interface SkuProductGroup {
  productId: number;
  productName: string;
  items: SwageazyInventorySkuLineItem[];
}

const groupSkuItemsByProduct = (
  items: SwageazyInventorySkuLineItem[],
): SkuProductGroup[] => {
  const groups = new Map<number, SkuProductGroup>();
  items.forEach((item) => {
    const existing = groups.get(item.product_id);
    if (existing) {
      existing.items.push(item);
    } else {
      groups.set(item.product_id, {
        productId: item.product_id,
        productName: item.product_name,
        items: [item],
      });
    }
  });
  return Array.from(groups.values());
};

const SwageazyInventoryOutwardPopup: React.FC<
  SwageazyInventoryOutwardPopupProps
> = ({ open, onClose, onSave }) => {
  const theme = useTheme();
  const { showError } = useNotification();
  const { swageazyInventoryStore, warehouseStore } = useStores();
  const [isResolvingBoxes, setIsResolvingBoxes] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      swageazyInventoryStore.resetOutwardRequestFormData();
      setIsResolvingBoxes(false);
      setResolveError(null);
      if (swageazyInventoryStore.apiTimer) {
        clearTimeout(swageazyInventoryStore.apiTimer);
        swageazyInventoryStore.clearApiTimer();
      }
    } else {
      handleWarehouseSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleWarehouseSearch = () => {
    if (warehouseStore.warehouses.length === 0) {
      warehouseStore.searchWarehouse().catch(() => {});
    }
  };

  const applyWarehouseFromPoOrDefault = () => {
    const detail = swageazyInventoryStore.purchaseRequestDetail;
    const poWarehouseId = detail?.warehouse_id
      ? String(detail.warehouse_id)
      : undefined;

    if (poWarehouseId) {
      const matchedWarehouse = warehouseStore.warehouses?.find(
        (warehouse) => String(warehouse.id) === poWarehouseId,
      );
      swageazyInventoryStore.setOutwardRequestFormData({
        warehouse_id: poWarehouseId,
        warehouse_name:
          matchedWarehouse?.identifier ||
          detail?.warehouse_identifier ||
          poWarehouseId,
        quantity_details: [],
      });
      return;
    }

    if (warehouseStore.warehouses.length === 1) {
      const onlyWarehouse = warehouseStore.warehouses[0];
      swageazyInventoryStore.setOutwardRequestFormData({
        warehouse_id: String(onlyWarehouse.id),
        warehouse_name: onlyWarehouse.identifier || String(onlyWarehouse.id),
        quantity_details: [],
      });
    }
  };

  const handlePoSearch = (searchText?: string) => {
    swageazyInventoryStore.clearApiTimer();
    if (searchText && searchText.length > 0) {
      const timer = setTimeout(() => {
        swageazyInventoryStore
          .getPurchaseOrderNumbers(searchText)
          .catch((err) => {
            showError(
              "Error",
              err?.response?.data?.message ||
                "Something went wrong! Please try again later.",
            );
          });
      }, 1000);
      swageazyInventoryStore.setApiTimer(timer);
    } else {
      swageazyInventoryStore.setPoSearchResults([]);
      swageazyInventoryStore.clearPurchaseRequestDetail();
      setResolveError(null);
      swageazyInventoryStore.setOutwardRequestFormData({
        po_id: undefined,
        sku_items: [],
        warehouse_id: undefined,
        warehouse_name: undefined,
        quantity_details: [],
      });
    }
  };

  const handlePoSelect = (selected: FilterValue[]) => {
    setResolveError(null);
    if (selected?.length > 0) {
      const poId = Number(selected[0]?.id);
      if (!isNaN(poId) && poId > 0) {
        swageazyInventoryStore.setOutwardRequestFormData({
          po_id: poId,
          sku_items: [],
          quantity_details: [],
        });
        swageazyInventoryStore
          .loadOutwardSkuItemsFromPo(poId)
          .then(() => {
            applyWarehouseFromPoOrDefault();
          })
          .catch((err) => {
            showError(
              "Error",
              err?.response?.data?.message ||
                "Failed to fetch the PO SKUs. Please try again later.",
            );
          });
      }
    } else {
      swageazyInventoryStore.clearPurchaseRequestDetail();
      swageazyInventoryStore.setOutwardRequestFormData({
        po_id: undefined,
        sku_items: [],
        warehouse_id: undefined,
        warehouse_name: undefined,
        quantity_details: [],
      });
      if (warehouseStore.warehouses.length === 1) {
        applyWarehouseFromPoOrDefault();
      }
    }
  };

  const isWarehouseLockedFromPo = Boolean(
    swageazyInventoryStore.purchaseRequestDetail?.warehouse_id,
  );

  const selectedWarehouseId =
    swageazyInventoryStore.outwardRequestFormData?.warehouse_id ||
    (isWarehouseLockedFromPo
      ? String(swageazyInventoryStore.purchaseRequestDetail?.warehouse_id)
      : undefined);

  const selectedWarehouseLabel = (() => {
    if (!selectedWarehouseId) return "";
    const matched = warehouseStore.warehouses?.find(
      (warehouse) => String(warehouse.id) === String(selectedWarehouseId),
    );
    return (
      matched?.identifier ||
      swageazyInventoryStore.outwardRequestFormData?.warehouse_name ||
      swageazyInventoryStore.purchaseRequestDetail?.warehouse_identifier ||
      String(selectedWarehouseId)
    );
  })();

  const skuItems =
    swageazyInventoryStore.outwardRequestFormData?.sku_items || [];
  const skuGroups = groupSkuItemsByProduct(skuItems);
  const quantityDetails =
    swageazyInventoryStore.outwardRequestFormData?.quantity_details || [];
  const hasResolvedBoxes = quantityDetails.length > 0;

  const getProductName = (productId: number, sku: string) => {
    const match = skuItems.find(
      (item) => item.product_id === productId && item.sku === sku,
    );
    return match?.product_name || match?.title || `Product #${productId}`;
  };

  const canResolveBoxes = (): boolean => {
    const formData = swageazyInventoryStore.outwardRequestFormData;
    if (!formData?.po_id) return false;
    if (!formData?.warehouse_id && !formData?.warehouse_name) return false;
    const items = formData?.sku_items || [];
    if (items.length === 0) return false;
    return items.some(
      (item) => Boolean(item.selected) && Number(item.quantity) > 0,
    );
  };

  const canSave = (): boolean => {
    const formData = swageazyInventoryStore.outwardRequestFormData;
    if (!formData?.po_id) return false;
    if (!formData?.warehouse_id && !formData?.warehouse_name) return false;

    const details = formData?.quantity_details || [];
    if (details.length === 0) return false;

    return details.some((detail) => Number(detail.quantity) > 0);
  };

  const handleResolveBoxes = () => {
    if (!canResolveBoxes() || isResolvingBoxes) return;

    setResolveError(null);
    setIsResolvingBoxes(true);
    swageazyInventoryStore
      .resolveOutwardBoxes()
      .then(() => {
        setResolveError(null);
      })
      .catch((err: any) => {
        const message =
          err?.response?.data?.message ||
          err?.message ||
          "Failed to resolve boxes. Please check quantities and try again.";
        setResolveError(message);
        showError("Resolve Boxes Failed", message);
      })
      .finally(() => {
        setIsResolvingBoxes(false);
      });
  };

  const handleSubmit = () => {
    if (!canSave()) return;
    setResolveError(null);
    onSave();
  };

  const primaryButtonStyles = (enabled: boolean) => ({
    backgroundColor: enabled
      ? theme.palette.customColors.EmeraldGreen
      : theme.palette.customColors.greyDarkborder,
    color: theme.palette.customColors.white,
    fontWeight: 600,
    padding: "8px 20px",
    borderRadius: "8px",
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      backgroundColor: enabled
        ? theme.palette.customColors.greenDark
        : theme.palette.customColors.greyDarkborder,
      transform: enabled ? "translateY(-1px)" : "none",
      boxShadow: enabled
        ? `0 4px 12px ${theme.palette.customColors.EmeraldGreen}30`
        : "none",
    },
    "&:disabled": {
      backgroundColor: theme.palette.customColors.greyDarkborder,
      color: theme.palette.customColors.white,
      cursor: "not-allowed",
    },
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      slotProps={{
        transition: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        backdrop: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        paper: {
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
            minHeight: { xs: "55vh", sm: "62vh", md: "68vh" },
            maxHeight: { xs: "92vh", sm: "90vh", md: "88vh", lg: "85vh" },
            maxWidth: {
              xs: "80vw",
              sm: "80vw",
              md: "70vw",
              lg: "60vw",
              xl: "50vw",
            },
            width: {
              xs: "80vw",
              sm: "80vw",
              md: "70vw",
              lg: "60vw",
              xl: "50vw",
            },
            margin: { xs: "16px", sm: "32px" },
            background: `linear-gradient(135deg, ${theme.palette.customColors.white} 0%, ${theme.palette.customColors.lightGreyColor} 100%)`,
          },
        },
      }}
      sx={{
        zIndex: 2000,
        "& .MuiDialog-container": {
          alignItems: "center",
          justifyContent: "center",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          position: "relative",
          height: "100%",
          minHeight: { xs: "55vh", sm: "62vh", md: "68vh" },
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${theme.palette.customColors.blueGradientStart} 0%, ${theme.palette.customColors.blueGradientEnd} 50%, ${theme.palette.customColors.blueGradientStart} 100%)`,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: { xs: "8px 16px", sm: "10px 20px" },
            borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <OutputIcon
                sx={{
                  color: theme.palette.customColors.blueGradientStart,
                  fontSize: 24,
                  marginTop: 0.2,
                }}
              />
              <CustomTypography variant="heading3">
                Swageazy Inventory Outward
              </CustomTypography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: theme.palette.customColors.greyTextColor,
              "&:hover": {
                backgroundColor: theme.palette.customColors.lightGreyColor,
                color: theme.palette.customColors.blackBorder,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent
          sx={{
            padding: { xs: "12px 16px", sm: "14px 20px" },
            flex: 1,
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            "&::-webkit-scrollbar": { width: "8px" },
            "&::-webkit-scrollbar-track": {
              backgroundColor: theme.palette.customColors.greyBackground,
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.customColors.greyDarkborder,
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: theme.palette.customColors.greyTextColor,
              },
            },
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Grid2 container spacing={1.5} alignItems="stretch">
              <Grid2
                size={{ xs: 12, sm: 6, md: 4 }}
                sx={{ display: "flex", flexDirection: "column" }}
              >
                <CustomTypography
                  variant="heading7"
                  style={{ fontWeight: 600, marginBottom: 6 }}
                >
                  PO Number{" "}
                  <span
                    style={{ color: theme.palette.error.main, marginLeft: 4 }}
                  >
                    *
                  </span>
                </CustomTypography>
                <AppSearchDropdownMulti
                  title="PO Number"
                  isMultiSelect={false}
                  list={swageazyInventoryStore.poSearchResults || []}
                  value={
                    swageazyInventoryStore.outwardRequestFormData?.po_id
                      ? [
                          {
                            id: String(
                              swageazyInventoryStore.outwardRequestFormData
                                ?.po_id,
                            ),
                            value: String(
                              swageazyInventoryStore.outwardRequestFormData
                                ?.po_id,
                            ),
                          },
                        ]
                      : []
                  }
                  handleSelect={handlePoSelect}
                  handleSearch={handlePoSearch}
                  local={false}
                  minWidth="100%"
                  borderRadius="8px"
                />
              </Grid2>

              <Grid2
                size={{ xs: 12, sm: 6, md: 4 }}
                sx={{ display: "flex", flexDirection: "column" }}
              >
                <CustomTypography
                  variant="heading7"
                  style={{ fontWeight: 600, marginBottom: 6 }}
                >
                  Warehouse{" "}
                  <span
                    style={{
                      color: theme.palette.error.main,
                      marginLeft: 4,
                    }}
                  >
                    *
                  </span>
                </CustomTypography>
                <AppSearchDropdownMulti
                  isMultiSelect={false}
                  title="Select Warehouse"
                  local
                  disabled={isWarehouseLockedFromPo}
                  backgroundColor={
                    theme.palette.customColors.grayBackGroundColor
                  }
                  borderColor={theme.palette.customColors.greyBodyBackground}
                  minWidth="13.938rem"
                  list={
                    warehouseStore.warehouses?.map((item) => {
                      return {
                        id: String(item?.id),
                        value: String(item?.identifier || item?.id),
                      };
                    }) || []
                  }
                  value={
                    selectedWarehouseId
                      ? [
                          {
                            id: String(selectedWarehouseId),
                            value: selectedWarehouseLabel,
                          },
                        ]
                      : []
                  }
                  handleSelect={(selectedItem: FilterValue[]) => {
                    if (isWarehouseLockedFromPo) return;
                    setResolveError(null);

                    if (selectedItem && selectedItem.length > 0) {
                      const warehouseUpdate: SwageazyInventoryOutwardRequestFormData =
                        {
                          warehouse_id: selectedItem[0].id,
                          warehouse_name: selectedItem[0].value,
                          quantity_details: [],
                        };
                      swageazyInventoryStore.setOutwardRequestFormData(
                        warehouseUpdate,
                      );
                    } else {
                      swageazyInventoryStore.setOutwardRequestFormData({
                        warehouse_id: undefined,
                        warehouse_name: undefined,
                        quantity_details: [],
                      });
                    }
                  }}
                />
              </Grid2>

              <Grid2
                size={{ xs: 12, sm: 12, md: 4 }}
                sx={{ display: "flex", flexDirection: "column" }}
              >
                <CustomTypography
                  variant="heading7"
                  style={{ fontWeight: 600, marginBottom: 6 }}
                >
                  Vendor Details
                </CustomTypography>
                <Box
                  sx={{
                    minHeight: 40,
                    border: `1px solid ${theme.palette.customColors.greyBorder}`,
                    borderRadius: "8px",
                    px: 1.5,
                    py: 0.75,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.25,
                    backgroundColor:
                      theme.palette.customColors?.lightGreyColor || "#F8FAFC",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <CustomTypography
                      variant="body2"
                      fontWeight={600}
                      sx={{ color: theme.palette.text.primary, minWidth: 88 }}
                    >
                      Vendor ID:
                    </CustomTypography>
                    <CustomTypography
                      variant="body2"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      {swageazyInventoryStore.purchaseRequestDetail
                        ?.vendor_id ?? "-"}
                    </CustomTypography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <CustomTypography
                      variant="body2"
                      fontWeight={600}
                      sx={{ color: theme.palette.text.primary, minWidth: 88 }}
                    >
                      Vendor Name:
                    </CustomTypography>
                    <CustomTypography
                      variant="body2"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      {swageazyInventoryStore.purchaseRequestDetail
                        ?.vendor_name || "-"}
                    </CustomTypography>
                  </Box>
                </Box>
              </Grid2>
            </Grid2>

            {swageazyInventoryStore.outwardRequestFormData?.po_id && (
              <Box sx={{ mt: 0.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 1,
                    flexWrap: "wrap",
                    gap: 0.5,
                  }}
                >
                  <CustomTypography
                    variant="heading7"
                    style={{
                      color: theme.palette.customColors.blackBorder,
                      fontWeight: 600,
                    }}
                  >
                    PO SKUs{" "}
                    <span
                      style={{
                        color: theme.palette.error.main,
                        marginLeft: 4,
                      }}
                    >
                      *
                    </span>
                  </CustomTypography>
                  <CustomTypography
                    variant="body3"
                    style={{ color: theme.palette.text.secondary }}
                  >
                    Select SKUs, enter outward qty, then Resolve Boxes.
                  </CustomTypography>
                </Box>

                {skuGroups.length === 0 ? (
                  <Box
                    sx={{
                      p: 1.5,
                      textAlign: "center",
                      color: theme.palette.customColors.greyTextColor,
                      border: `1px dashed ${theme.palette.customColors.greyBorder}`,
                      borderRadius: "8px",
                      backgroundColor:
                        theme.palette.customColors.lightGreyColor,
                    }}
                  >
                    <CustomTypography variant="body1">
                      No SKUs found for this PO
                    </CustomTypography>
                  </Box>
                ) : (
                  skuGroups.map((group) => {
                    const selectedCount = group.items.filter(
                      (item) => item.selected,
                    ).length;
                    const allSelected =
                      group.items.length > 0 &&
                      selectedCount === group.items.length;
                    const someSelected =
                      selectedCount > 0 && selectedCount < group.items.length;

                    return (
                      <Box
                        key={group.productId}
                        sx={{
                          marginBottom: 1,
                          padding: { xs: 1, sm: 1.25 },
                          border: `1px solid ${theme.palette.customColors.greyBorder}`,
                          borderRadius: "8px",
                          backgroundColor:
                            theme.palette.customColors.lightGreyColor,
                          "&:last-of-type": { marginBottom: 0 },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            flexWrap: "wrap",
                            mb: 0.75,
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={allSelected}
                                indeterminate={someSelected}
                                onChange={(e) => {
                                  setResolveError(null);
                                  swageazyInventoryStore.setOutwardSkuItemsSelected(
                                    group.items.map((item) => ({
                                      sku: item.sku,
                                      productId: item.product_id,
                                    })),
                                    e.target.checked,
                                  );
                                }}
                                sx={{
                                  p: 0.25,
                                  "& .MuiSvgIcon-root": { fontSize: 16 },
                                }}
                              />
                            }
                            label={
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  flexWrap: "wrap",
                                  pl: 1,
                                }}
                              >
                                <SkuCopyRow
                                  sku={group.productName}
                                  emptyFallback="-"
                                  copyLabel="Copy name"
                                  fontWeight={600}
                                  color={theme.palette.text.primary}
                                  variant="heading7"
                                  allowWrap
                                />
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  label={`${group.items.length} ${
                                    group.items.length === 1 ? "SKU" : "SKUs"
                                  }`}
                                />
                              </Box>
                            }
                            sx={{ mr: 0, ml: 0, my: 0, alignItems: "center" }}
                          />
                        </Box>

                        {group.items.map((item) => (
                          <Box
                            key={`${item.product_id}-${item.sku}`}
                            sx={{
                              mb: 1,
                              p: { xs: 1, sm: 1.25 },
                              border: `1px solid ${theme.palette.customColors.greyBorder}`,
                              borderRadius: "8px",
                              backgroundColor: theme.palette.customColors.white,
                              opacity: item.selected ? 1 : 0.72,
                              "&:last-of-type": { mb: 0 },
                            }}
                          >
                            <Grid2
                              container
                              spacing={1.5}
                              sx={{ width: "100%", alignItems: "center" }}
                            >
                              <Grid2
                                size={{ xs: 12, sm: 8, md: 8 }}
                                sx={{ minWidth: 0 }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 1,
                                  }}
                                >
                                  <Checkbox
                                    size="small"
                                    checked={Boolean(item.selected)}
                                    onChange={(e) => {
                                      setResolveError(null);
                                      swageazyInventoryStore.updateOutwardSkuItem(
                                        item.sku,
                                        item.product_id,
                                        { selected: e.target.checked },
                                      );
                                    }}
                                    sx={{
                                      p: 0.25,
                                      mt: 0,
                                      ml: -0.5,
                                      "& .MuiSvgIcon-root": { fontSize: 16 },
                                    }}
                                  />
                                  <Box sx={{ minWidth: 0, maxWidth: "100%" }}>
                                    <SkuCopyRow
                                      sku={
                                        item.title ||
                                        item.product_name ||
                                        item.sku
                                      }
                                      emptyFallback="-"
                                      copyLabel="Copy name"
                                      fontWeight={600}
                                      color={theme.palette.text.primary}
                                      variant="body2"
                                      allowWrap
                                    />
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        flexWrap: "wrap",
                                      }}
                                    >
                                      <SkuCopyRow
                                        sku={item.sku}
                                        emptyFallback="No SKU"
                                        copyLabel="Copy SKU"
                                        variant="body3"
                                        allowWrap
                                      />
                                      {item.expected_quantity !== undefined && (
                                        <CustomTypography
                                          variant="body3"
                                          style={{
                                            color: theme.palette.text.secondary,
                                          }}
                                        >
                                          PR Qty: {item.expected_quantity}
                                        </CustomTypography>
                                      )}
                                    </Box>
                                  </Box>
                                </Box>
                              </Grid2>
                              <Grid2
                                size={{ xs: 12, sm: 4, md: 4 }}
                                sx={{ minWidth: 0 }}
                              >
                                <CustomTextField
                                  width="100%"
                                  label="Qty Outward"
                                  placeholder="Qty Outward"
                                  value={item.quantity ?? ""}
                                  disabled={!item.selected}
                                  onChange={(e) => {
                                    setResolveError(null);
                                    swageazyInventoryStore.updateOutwardSkuItem(
                                      item.sku,
                                      item.product_id,
                                      {
                                        quantity: e.target.value
                                          ? Number(e.target.value)
                                          : undefined,
                                      },
                                    );
                                  }}
                                  type="number"
                                  allowNegativeValue={false}
                                  allowDecimalValue={false}
                                />
                              </Grid2>
                            </Grid2>
                          </Box>
                        ))}
                      </Box>
                    );
                  })
                )}
              </Box>
            )}
          </Box>

          {resolveError && (
            <Box
              sx={{
                p: 1.25,
                borderRadius: "8px",
                border: `1px solid ${theme.palette.error.light}`,
                backgroundColor: `${theme.palette.error.main}10`,
              }}
            >
              <CustomTypography
                variant="body2"
                style={{ color: theme.palette.error.main }}
              >
                {resolveError}
              </CustomTypography>
            </Box>
          )}

          {hasResolvedBoxes && (
            <>
              <Divider />
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 1,
                    flexWrap: "wrap",
                    gap: 0.5,
                  }}
                >
                  <CustomTypography
                    variant="heading7"
                    style={{
                      color: theme.palette.customColors.blackBorder,
                      fontWeight: 600,
                    }}
                  >
                    Resolved Boxes
                  </CustomTypography>
                  <CustomTypography
                    variant="body3"
                    style={{ color: theme.palette.text.secondary }}
                  >
                    You can lower quantities, but not increase them above the
                    allocated stock.
                  </CustomTypography>
                </Box>

                {quantityDetails.map((detail, index) => (
                  <Box
                    key={`${detail.product_id}-${detail.sku}-${detail.box_id}-${index}`}
                    sx={{
                      mb: 1,
                      p: { xs: 1, sm: 1.25 },
                      border: `1px solid ${theme.palette.customColors.greyBorder}`,
                      borderRadius: "8px",
                      backgroundColor: theme.palette.customColors.white,
                      "&:last-of-type": { mb: 0 },
                    }}
                  >
                    <Grid2
                      container
                      spacing={1.5}
                      sx={{ width: "100%", alignItems: "center" }}
                    >
                      <Grid2
                        size={{ xs: 12, sm: 4, md: 4 }}
                        sx={{ minWidth: 0 }}
                      >
                        <SkuCopyRow
                          sku={getProductName(detail.product_id, detail.sku)}
                          emptyFallback="-"
                          copyLabel="Copy name"
                          fontWeight={600}
                          color={theme.palette.text.primary}
                          variant="body2"
                          allowWrap
                        />
                        <SkuCopyRow
                          sku={detail.sku}
                          emptyFallback="No SKU"
                          copyLabel="Copy SKU"
                          variant="body3"
                          allowWrap
                        />
                      </Grid2>
                      <Grid2
                        size={{ xs: 12, sm: 4, md: 4 }}
                        sx={{ minWidth: 0 }}
                      >
                        <CustomTextField
                          width="100%"
                          label="Box ID"
                          placeholder="Box ID"
                          value={detail.box_id || ""}
                          disabled
                          type="text"
                        />
                      </Grid2>
                      <Grid2
                        size={{ xs: 12, sm: 4, md: 4 }}
                        sx={{ minWidth: 0 }}
                      >
                        <CustomTextField
                          width="100%"
                          label="Qty"
                          placeholder="Qty"
                          value={detail.quantity ?? ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            swageazyInventoryStore.updateOutwardQuantityDetail(
                              index,
                              value === "" ? undefined : Number(value),
                            );
                          }}
                          type="number"
                          allowNegativeValue={false}
                          allowDecimalValue={false}
                        />
                        {detail.max_quantity !== undefined && (
                          <CustomTypography
                            variant="body3"
                            style={{
                              color: theme.palette.text.secondary,
                              marginTop: 2,
                            }}
                          >
                            Max: {detail.max_quantity}
                          </CustomTypography>
                        )}
                      </Grid2>
                    </Grid2>
                  </Box>
                ))}
              </Box>
            </>
          )}

          <Box
            sx={{
              mb: 0,
              p: 1.25,
              backgroundColor: theme.palette.customColors.lightGreyColor,
              borderRadius: "8px",
              border: `1px solid ${theme.palette.customColors.greyBorder}`,
            }}
          >
            <CustomTypography
              variant="body3"
              style={{
                color: theme.palette.text.secondary,
                lineHeight: "1.4",
                display: "flex",
                alignItems: "flex-start",
                gap: "8px",
              }}
            >
              <span
                style={{ fontWeight: 600, color: theme.palette.text.primary }}
              >
                Note:
              </span>
              <span>
                Select PO and warehouse, check the SKUs to outward with qty,
                then Resolve Boxes. Review allocated box lines and Save to debit
                inventory.
              </span>
            </CustomTypography>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            padding: { xs: "8px 16px 10px", sm: "10px 20px 12px" },
            borderTop: `1px solid ${theme.palette.customColors.greyBorder}`,
            backgroundColor: theme.palette.customColors.lightGreyColor,
            justifyContent: "flex-end",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <AppButton
            onClick={onClose}
            variant="grayscale"
            label="Cancel"
            customStyles={{
              borderColor: theme.palette.customColors.greyDarkborder,
              color: theme.palette.customColors.greyTextColor,
              fontWeight: 600,
              padding: "8px 20px",
              borderRadius: "8px",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                transform: "translateY(-1px)",
              },
            }}
          />

          <AppButton
            onClick={handleResolveBoxes}
            variant="primary"
            label={isResolvingBoxes ? "Resolving..." : "Resolve Boxes"}
            disabled={!canResolveBoxes() || isResolvingBoxes}
            customStyles={primaryButtonStyles(
              canResolveBoxes() && !isResolvingBoxes,
            )}
          />

          <AppButton
            onClick={handleSubmit}
            variant="primary"
            label="Save"
            disabled={!canSave() || isResolvingBoxes}
            customStyles={primaryButtonStyles(canSave() && !isResolvingBoxes)}
          />
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default observer(SwageazyInventoryOutwardPopup);
